import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { useSessionPmsAccount } from '../hooks/useSessionPmsAccount';
import { getVendorDefinition } from '../vendor-settings/vendorRegistry';
import { ConnectionHeroCard } from './ConnectionHeroCard';
import { landingCopy as copy } from './copy';
import { FirstRunLanding } from './FirstRunLanding';
import { LandingFooter } from './LandingFooter';
import { VendorDirectory } from '../components/VendorDirectory';
import { ComingSoonInterestLink } from './ComingSoonInterestLink';

/**
 * /home/integrations (spec §4). One credential-free read decides the layout:
 * `none` (or a failed status read — the hook fails open) is first-run;
 * anything connected gets the hero + directory. The status slice's
 * `pmsSource` is server data, not guaranteed to be a known registry key, so
 * the layout also falls open to first-run when it doesn't resolve — same
 * fail-open posture as the `isError` path. The shell's <main> is the scroll
 * region, so this root is `min-h-full` — no `100vh` math (the old VendorsList
 * hazard in the responsive roadmap). Spacing follows the prototype: 32 px
 * page padding, 28 px between blocks, content capped at `max-w-7xl` (the app-wide container width; the prototype used 1400 px).
 */
export const IntegrationsLanding = () => {
  const account = useSessionPmsAccount();
  const { status, isLoading, canManage } = useIntegrationStatus(account);
  const connected = status.kind !== 'none' && getVendorDefinition(status.pmsSource) ? status : null;
  return (
    <section className="flex min-h-full flex-col bg-background">
      <header className="shrink-0 border-b border-border bg-background px-4 py-5 lg:px-8">
        <Typography as="h1" variant="heading-xl">{copy.header}</Typography>
      </header>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="flex max-w-7xl flex-col gap-7 p-4 lg:p-8">
          {connected === null ? (
            <FirstRunLanding />
          ) : (
            <>
              <ConnectionHeroCard status={connected} canManage={canManage} account={account} />
              <VendorDirectory connected={connected} copy={copy.directory} comingSoonFooter={<ComingSoonInterestLink />} />
            </>
          )}
          <LandingFooter />
        </div>
      )}
    </section>
  );
};

export default IntegrationsLanding;
